/** B4 index overlap. Keep the two strokes identical to packages/brand/mark.svg. */
export function Mark({
	className = "mark",
	label,
}: {
	className?: string;
	label?: string;
}) {
	return (
		<svg
			className={className}
			viewBox="0 0 32 32"
			role={label ? "img" : undefined}
			aria-label={label}
			aria-hidden={label ? undefined : true}
			focusable="false"
		>
			{label && <title>{label}</title>}
			<g fill="currentColor">
				<path d="M12.6 6C6.5 6 2 11.2 2 18.2C2 24.7 6.4 30 12.4 30C13.5 30 14 29.5 14 28.4V26.8C14 25.2 13.6 24.6 12.2 24.3C9.1 23.7 8 21.2 8 18.2C8 14.6 9.7 12.2 12.8 12.2H15.7C16 12.2 16.2 12 16.2 11.7V10C16.2 7.4 15.1 6 12.6 6Z" />
				<path d="M18.4 2C25.1 2 30 7.5 30 14.4C30 21.5 25.4 27.4 19 27.4C15.8 27.4 13.6 25.7 13.6 22.1V13.8C13.6 13.6 13.8 13.4 14 13.4H16.2V19.7C16.2 21.1 17.1 21.9 18.5 21.9C21.6 21.9 23.2 19.2 23.2 15.4V13.2C23.2 9.9 21.9 7.5 18.6 7.3C17.2 7.2 16.8 6.6 16.8 5.3V3.6C16.8 2.5 17.3 2 18.4 2Z" />
			</g>
		</svg>
	);
}

/** How much room the lockup has, not how important it is. */
export type LogoSize = "sm" | "md" | "lg" | "xl";
/** What of the lockup is drawn. */
export type LogoVariant = "lockup" | "mark" | "word" | "stack";
/** What the word is sitting on. */
export type LogoTone = "brand" | "invert" | "mono";

/**
 * The lockup: the mark and the name, everywhere the product signs itself.
 *
 * It draws no link and no layout of its own — the sidebar's brand row and the
 * footer's brand column want different padding around the same lockup — so a
 * caller wraps it in whatever anchor its router uses and this stays a picture
 * of a name.
 *
 * The word is real text rather than an SVG path: it is what a screen reader
 * reads and what find-in-page finds, and the mark beside it is decoration.
 * Only the mark-only arrangement carries the name in an `aria-label`, because
 * there the mark is the whole logo.
 *
 * The geometry — the mark's size against the type, the gap, the sizes — is in
 * `packages/tokens/logo.css`, which is the only file that knows any of it.
 */
export function Logo({
	size = "md",
	variant = "lockup",
	tone = "brand",
	className,
}: {
	size?: LogoSize;
	variant?: LogoVariant;
	tone?: LogoTone;
	className?: string;
}) {
	const word = variant !== "mark";
	return (
		<span className={className ? `logo ${className}` : "logo"} data-size={size} data-variant={variant} data-tone={tone}>
			{variant !== "word" && <Mark className="logo-seal" label={word ? undefined : "Ondit"} />}
			{word && <span className="logo-word">Ondit</span>}
		</span>
	);
}
